<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import AnalyticsPanel from './AnalyticsPanel.vue';
import MetricValue from './MetricValue.vue';
import { useAnalyticsFormat } from './useAnalyticsFormat';

const props = defineProps({
  followups: { type: Object, default: null }, // { sent, replied, sending_enabled }
  runtimeReason: { type: String, default: '' },
});

const { t } = useI18n();
const { number } = useAnalyticsFormat();

// Com o envio desligado na plataforma, "0 enviados" leria como "a IA não precisou
// mandar nenhum". A tela diz que está desligado; os números só aparecem se algum
// follow-up saiu no período, antes do desligamento.
const sendingOff = computed(() => props.followups?.sending_enabled === false);
const showNumbers = computed(
  () => !sendingOff.value || props.followups?.sent > 0
);

const stats = computed(() => [
  {
    key: 'sent',
    label: t('ANALYTICS_REPORTS.FOLLOWUPS.SENT'),
    value: props.followups?.sent ?? null,
  },
  {
    key: 'replied',
    label: t('ANALYTICS_REPORTS.FOLLOWUPS.REPLIED'),
    value: props.followups?.replied ?? null,
  },
]);
</script>

<template>
  <AnalyticsPanel
    :title="t('ANALYTICS_REPORTS.FOLLOWUPS.TITLE')"
    :description="t('ANALYTICS_REPORTS.FOLLOWUPS.DESCRIPTION')"
  >
    <MetricValue v-if="!followups" :reason="runtimeReason" />
    <template v-else>
      <p
        v-if="sendingOff"
        data-test-id="analytics-followups-off"
        class="flex gap-2 items-start mb-0 text-sm text-n-slate-12"
      >
        <Icon
          icon="i-lucide-power-off"
          class="mt-0.5 size-4 shrink-0 text-n-slate-11"
        />
        {{ t('ANALYTICS_REPORTS.FOLLOWUPS.DISABLED') }}
      </p>
      <dl v-if="showNumbers" class="flex flex-wrap gap-x-8 gap-y-2">
        <div v-for="stat in stats" :key="stat.key" class="min-w-0">
          <dt class="text-xs text-n-slate-11">{{ stat.label }}</dt>
          <dd>
            <MetricValue
              :value="stat.value"
              :format="number"
              :reason="runtimeReason"
              class="text-xl font-medium tabular-nums text-n-slate-12"
            />
          </dd>
        </div>
      </dl>
    </template>
  </AnalyticsPanel>
</template>
